import { snap, clampOptional } from '../utils/math.js';
import { REPLACE_MAP } from './formatter.constants.js';

const DEFAULT_SMALL_NUDGE = 1.0;
const DEFAULT_BIG_NUDGE = 10.0;
const NUMBER_REGEX = /^-?[\d]*\.?[\d]*$/;
/** A finite, non-NaN number — safe to format or expose via aria-value*. */
function isValidNumber(value) {
    return typeof value === 'number' && !Number.isNaN(value) && Number.isFinite(value);
}
/**
 * Normalize a string we expect to be a number by converting other localizations
 * of digits to 0-9, and other forms of punctuation to ascii comma.
 */
function normalize(str) {
    if (str === '' || NUMBER_REGEX.test(str))
        return str;
    // ignore too many decimals, rather than break on them
    let hasDecimal = false;
    let output = '';
    for (let i = 0; i < str.length; ++i) {
        const char = str[i];
        const next = REPLACE_MAP[char];
        // for unexpected characters, return original string to throw error
        if (next == null)
            return str;
        if (next === '.') {
            if (!hasDecimal) {
                output += next;
                hasDecimal = true;
            }
            continue;
        }
        // Reset hasDecimal when we encounter a minus sign, since it indicates a new number
        if (next === '-') {
            hasDecimal = false;
        }
        output += next;
    }
    return output;
}
/**
 * Determine if the given formatter is purely a WriteFormatter, or an
 * IncrementFormatter with an `incrementBy` method.
 */
function isIncrementFormatter(formatter) {
    return 'incrementBy' in formatter;
}
function getFormatterIncrementTargets(formatter, el) {
    if (!formatter.getIncrementTargets)
        return null;
    // Use the current cursor/selection position to determine which parts of the input to increment
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    return formatter.getIncrementTargets(el.value, { start, end });
}
function getFormatterNudgeAmount(formatter, big, initialValue) {
    if (formatter.getNudgeAmount)
        return formatter.getNudgeAmount(big, initialValue);
    return big ? DEFAULT_BIG_NUDGE : DEFAULT_SMALL_NUDGE;
}
function doFormatterIncrementBy(formatter, initialValue, amount, { big = false, incrementTargets = null, snap: doSnap } = {}) {
    let preClamped = formatter.incrementBy(initialValue, amount, incrementTargets);
    if (doSnap) {
        const resolution = getFormatterNudgeAmount(formatter, big, initialValue);
        if (formatter.snap) {
            preClamped = formatter.snap(preClamped, resolution);
        }
        else if (typeof preClamped === 'number') {
            // have to cast as any here because TS thinks it's unsafe to assign a number to Out,
            // but we've already checked that Out is a number
            preClamped = snap(preClamped, resolution);
        }
    }
    const value = doFormatterClamp(formatter, preClamped);
    return { value, preClamped };
}
function doFormatterIncrementByNudgeAmount(formatter, value, direction, opts = {}) {
    const amount = getFormatterNudgeAmount(formatter, opts.big ?? false, value);
    return doFormatterIncrementBy(formatter, value, amount * direction, opts);
}
function doFormatterParse(formatter, valueIn, currentValue) {
    const parsedValue = formatter.parse(valueIn, currentValue);
    const value = isIncrementFormatter(formatter)
        ? doFormatterClamp(formatter, parsedValue)
        : parsedValue;
    return { value, parsedValue };
}
/**
 * Call when parsing is expected to throw. This will call the formatter's `onParseThrow` method
 * with any thrown error, and handle massaging the results into a format that is easy to use.
 *
 * For example, if `onParseThrow` returns a non-function value, it should be as if the parse
 * method returned that value, and the caller doesn't need to duplicate any logic.
 *
 * If this returns null, the caller should do nothing.
 */
function doFormatterSafeParse(formatter, str, current, source, event = null) {
    try {
        const result = doFormatterParse(formatter, str, current);
        return { ...result, callback: null };
    }
    catch (error) {
        const value = formatter.onParseThrow?.(str, { error, value: current, source, event });
        if (typeof value === 'function') {
            const callback = value;
            return { value: null, parsedValue: null, callback };
        }
        return value == null ? null : { value, parsedValue: value, callback: null };
    }
}
function doFormatterClamp(formatter, value) {
    const { min, max } = formatter;
    if (formatter.clamp)
        return formatter.clamp(value, min, max);
    if (typeof value === 'number')
        return clampOptional(value, min, max);
    return value;
}
function looksEqual(formatter, val1, val2) {
    if (val1 === val2)
        return true;
    // We can't rely on the values themselves, because floats (and other types that use floats)
    // are usually off by some epsilon which is complicated to define. So if the value looks the
    // same to the user, it's equal enough for us! We've already proven that neither val1 or val2
    // are MIXED at this point, so it is safe to cast to `TValue`.
    if (formatter.isEqual)
        return formatter.isEqual(val1, val2);
    return formatter.format(val1) === formatter.format(val2);
}
function getCanIncrement(query, formatter, val) {
    const result = formatter.canIncrement?.(val) ?? 'all';
    switch (result) {
        case 'all':
            return true;
        case 'none':
            return false;
        case 'nudge':
        case 'scrub':
            return result === query;
    }
}

export { DEFAULT_BIG_NUDGE, DEFAULT_SMALL_NUDGE, doFormatterClamp, doFormatterIncrementBy, doFormatterIncrementByNudgeAmount, doFormatterParse, doFormatterSafeParse, getCanIncrement, getFormatterIncrementTargets, getFormatterNudgeAmount, isIncrementFormatter, isValidNumber, looksEqual, normalize };
//# sourceMappingURL=formatter.utils.js.map
